import { noop, times } from 'lodash-es';
import React, { useCallback, useEffect, useRef, useState } from 'react';

import useObserve from 'hooks/use-observe';
import usePersistent from 'hooks/use-persistent';

import useStyles from './styles';

interface Props {
    index: number;
    count: number;
    orientation?: 'vertical' | 'horizontal';
    enabled?: boolean;
    onClick?: (index: number) => void;
    onSlide?: () => void;
    className?: string;
}

const DURATION = 5_000;

const Slider: React.FC<Props> = ({
    count = 0,
    index = 0,
    enabled = count > 0,
    orientation = 'vertical',
    onClick = noop,
    onSlide = noop,
    className
}) => {
    const { classes, cx } = useStyles({ orientation });

    const onSlideRef = usePersistent(onSlide);
    const mutableRef = useRef<{ raf: number; ts: number; elapsed: number }>({ raf: NaN, ts: NaN, elapsed: NaN });
    const [fraction, setFraction] = useState(0);

    const clear = useCallback(() => {
        cancelAnimationFrame(mutableRef.current.raf);
        mutableRef.current.raf = NaN;
        mutableRef.current.ts = NaN;
        mutableRef.current.elapsed = NaN;
        setFraction(0);
    }, []);

    const animate = useCallback(
        (time: number) => {
            mutableRef.current.ts ||= time;
            mutableRef.current.elapsed ||= 0;

            mutableRef.current.elapsed += time - mutableRef.current.ts;
            mutableRef.current.ts = time;

            if (mutableRef.current.elapsed < DURATION) {
                setFraction(mutableRef.current.elapsed / DURATION);
                mutableRef.current.raf = window.requestAnimationFrame(animate);
            } else {
                clear();
                onSlideRef.current?.();
            }
        },
        [clear, onSlideRef]
    );

    const run = useCallback(() => {
        mutableRef.current.ts = NaN;
        mutableRef.current.raf = window.requestAnimationFrame(animate);
    }, [animate]);

    const pause = useCallback(() => {
        mutableRef.current.ts = NaN;
        cancelAnimationFrame(mutableRef.current.raf);
    }, []);

    useObserve({
        subject: enabled,
        callback: (value) => {
            if (value) {
                run();
            } else {
                pause();
            }
        }
    });

    useObserve({
        subject: index,
        callback: () => {
            clear();
            run();
        }
    });

    useEffect(() => {
        if (enabled) {
            run();
        }

        return () => clear();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className={cx(classes.root, className)}>
            {times(count, (number) => {
                const isActive = number === index;

                return (
                    <span
                        key={number}
                        className={cx(classes.point, { [classes.active]: isActive })}
                        onClick={isActive ? undefined : () => onClick(number)}
                    >
                        <span
                            className={cx(classes.progress, { [classes.active]: isActive })}
                            style={{
                                ...(isActive && orientation === 'vertical'
                                    ? { height: `${Math.round(fraction * 100)}%` }
                                    : {}),
                                ...(isActive && orientation === 'horizontal'
                                    ? { width: `${Math.round(fraction * 100)}%` }
                                    : {})
                            }}
                        />
                    </span>
                );
            })}
        </div>
    );
};

export default Slider;
